import { IkeUnit, IkeGeodesic } from '@ikegps/core-measurement';
import { type CaptureFileJSONObject, IkeToken, isCollectionVariant } from '@ikegps/core-api';
import type { DistanceBearingPitch, LocationData, Point } from '@ikegps/core-api';
import {
	type CollectionModel,
	CollectionModelFieldAnchor,
	type CollectionModelFieldVector,
	SpanType,
} from '@ikegps/core-shared';
import { noop, pick } from 'lodash';
import { get } from 'lodash';
import { action, computed, observable, makeObservable } from 'mobx';
import { IMithrilComponent } from '../../components/mithril.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { T, Translate } from '../../i18n';
import { addStyles, assetPath, DefaultTheme } from '../../components/theme';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { classNames } from '../../util/classnames';
import { PointCache } from '../google/projection.cache';
import type { MeasurementState, MeasurementPoint } from '../../state/measurement.state';
import type { DistanceMeasureState } from '../../state/distance.measure.state';
import type { LineDrawState } from '../../state/line.draw.state';
import { lineDrawNodes as buildLineDrawNodes } from '../../components/line.field.polyline';
import { MapPointDragger } from '../../components/map.point.drag';
import { GenericMapLine } from './map.line.generic';
import { isLabelTypeVisibleAtZoom, readDisplaySettings, writeDisplaySettings } from './map.display.settings';
import {
	type LegendItemId,
	legendItemIdForPoleAccuracy,
	legendItemIdForSpanKind,
	lineTypeVisibilityKey,
} from './map.legend';
import { LineCSS, MapLine, type MaybeSpanType } from './map.line/map.line';
import { MapPolyline } from './map.polyline/map.polyline';
import { MapPoint, TopologicalEditorPointCSS } from './map.point/map.point';
import { TopologicalPointCollectionCache } from './point.collection.cache';
import { AppStore } from '../../store';

const CSS = {
	container: 'c-PhotomeasureOverlay',
	orca: 'c-PhotomeasureOverlay__device',
	orcaIcon: 'c-PhotomeasureOverlay__orcaIcon',
	fov: 'c-PhotomeasureOverlay__fov',
	fovSprite: 'c-PhotomeasureOverlay__fovSprite',
	label: 'c-PhotomeasureOverlay__label',
	labelPill: 'c-MapOverlayLabelPill',
	layers: 'c-MapOverlayLayers',
	layersFocusActive: 'c-MapOverlayLayers--focusActive',
	line: 'c-MapOverlayLayer__line',
	point: 'c-MapOverlayLayer__point',
	labelLayer: 'c-MapOverlayLayer__labels',
	hidden: 'c-MapOverlayLayer--hidden',
};

const Style = {
	[CSS.container]: {
		position: 'absolute',
	},
	[CSS.orca]: {
		position: 'absolute',
		transform: 'translate3d(-16px, -16px, -1px)',
		width: '32px',
		height: '32px',
		borderRadius: '16px',
		boxShadow: DefaultTheme.shadow.box.dp4,
	},
	[CSS.orcaIcon]: {
		width: '32px',
		height: '32px',
		transformOrigin: '50% 50%',
	},
	[CSS.fov]: {
		position: 'absolute',
		left: 0,
		top: 0,
		transform: 'translate3d(-75px, -129px, -1px)',
	},
	[CSS.fovSprite]: {
		width: '150px',
		height: '129px',
		transformOrigin: '50% 100%',
	},
	// Black readout pill shared by the device label and the measurement-line distance labels;
	// positioning stays per-use
	[CSS.labelPill]: {
		position: 'absolute',
		backgroundColor: 'rgba(0, 0, 0, 0.7)',
		color: '#FFFFFF',
		padding: '2px 6px',
		borderRadius: '3px',
		fontSize: '11px',
		fontWeight: 500,
		whiteSpace: 'nowrap',
		pointerEvents: 'none',
	},
	// Centered under the device icon
	[CSS.label]: {
		top: '20px',
		transform: 'translate3d(-50%, 0, -1px)',
	},
	[CSS.hidden]: {
		display: 'none',
	},
	// Vector labels render in their own layer above all line strokes so a line never paints over a label.
	// `isolation` contains the raised-label z-index so a lifted label can't escape above the point layers.
	[CSS.labelLayer]: {
		isolation: 'isolate',
		pointerEvents: 'none',
	},
	// Map-wide focus dim: one class write on the wrapper fades only the LABELS of non-focused features
	// so a hovered/selected label stands out. Vector line strokes and pole markers stay fully visible;
	// the focused label is spared via `labelRaised` / `focusExempt` (pole titles). One DOM write, not N.
	[`${CSS.layersFocusActive} .${LineCSS.label}:not(.${LineCSS.labelRaised})`]: {
		opacity: 0.25,
	},
	[`${CSS.layersFocusActive} .${TopologicalEditorPointCSS.titleContainer}:not(.${TopologicalEditorPointCSS.focusExempt})`]:
		{
			opacity: 0.25,
		},
};

addStyles(Style);

interface ITopologicalEditorMapOverlayOptions {
	map: google.maps.Map;
	onCollectionClick: (id: string) => void;
	onVectorClick: (collectionId: string, vectorId: string) => void;
	selectedCollectionRevision?: string | null;
	collections?: CollectionModel[];
	onVectorDragStart?: () => void;
	onVectorDragEnd?: (
		field: CollectionModelFieldVector | CollectionModelFieldAnchor,
		nextVector: DistanceBearingPitch
	) => void;
	measurementState?: MeasurementState;
	distanceMeasureState?: DistanceMeasureState;
	lineDrawState?: LineDrawState;
}

export class TopologicalEditorMapOverlay extends IMithrilComponent {
	private overlay: google.maps.OverlayView;
	private origin = new google.maps.LatLng(0, 0);
	private el: Element | null = null;

	@observable bounds: google.maps.LatLngBounds | undefined = undefined;
	boundString = observable.box('', { name: 'BoundsString' });
	@observable zoom: number | undefined = undefined;

	@observable private collections: CollectionModel[] = [];
	@observable private selectedCollectionRevision: string | null = null;
	@observable private selectedVectorId: string | null = null;
	@observable public visible = true;
	@observable public collectionHoverRevision: string | null = null;
	@observable public collectionAndVectorHoverId: string | null = null;
	// Label visibility is restored from persisted display settings in the constructor (default off).
	@observable public viewCollectionLabels = false;
	@observable public viewVectorLabels = false;
	@observable public viewVectors = true;
	@observable public viewLines = true;
	@observable public hideTitles = false;
	/** Print capture sets this so the print modal's label checkboxes apply at any zoom. Not persisted. */
	@observable public bypassLabelZoomGating = false;
	/** Print capture sets this to suppress the focus dim so no feature prints faded. Not persisted. */
	@observable public suppressFocusDim = false;
	@observable public hiddenLegendItems: LegendItemId[] = [];
	@observable public hiddenLineTypes: string[] = [];

	private measurementState?: MeasurementState;
	private distanceMeasureState?: DistanceMeasureState;
	private lineDrawState?: LineDrawState;

	/** Drag handling for measurement / distance / line-draw vertices (shared with the other map surfaces). */
	private readonly pointDragger = new MapPointDragger(() => this.overlay.getProjection());

	@observable public selectedVectorMoveDelta: Point = {
		x: 0,
		y: 0,
	};
	@observable public selectedVectorInitialMove: Point | null = null;

	@observable selectedCapture: CaptureFileJSONObject | null = null;

	name = 'TopologicalEditorMapOverlay';

	private boundVectorHandleMouseMove: (event: MouseEvent) => void;
	private boundVectorHandleMouseUp: (event: MouseEvent) => void;

	constructor(private options: ITopologicalEditorMapOverlayOptions) {
		super();

		makeObservable(this);

		const displaySettings = readDisplaySettings();
		this.hiddenLegendItems = displaySettings.hiddenLegendItems;
		this.hiddenLineTypes = displaySettings.hiddenLineTypes;
		// Restore the persisted label-visibility choice (default off) so Map Mode reopens as the user left it.
		this.viewCollectionLabels = displaySettings.viewLabels;
		this.viewVectorLabels = displaySettings.viewLabels;

		const { collections, selectedCollectionRevision, measurementState, distanceMeasureState, lineDrawState } = options;

		this.overlay = new google.maps.OverlayView();
		this.overlay.setMap(options.map);

		this.selectedCollectionRevision = selectedCollectionRevision || null;
		this.collections = collections || [];
		this.measurementState = measurementState;
		this.distanceMeasureState = distanceMeasureState;
		this.lineDrawState = lineDrawState;

		this.overlay.onAdd = () => {
			const panes = this.overlay.getPanes();
			if (panes == null) {
				return;
			}
			this.el = panes.overlayMouseTarget;
			Services.Render.mount(this.el, this, this.name);
		};

		this.overlay.onRemove = () => {
			if (this.el) {
				Services.Render.mount(this.el, null as any, this.name);
			}
		};

		this.overlay.draw = this.onDraw.bind(this);

		this.boundVectorHandleMouseMove = this.onVectorHandleDragMove.bind(this);
		this.boundVectorHandleMouseUp = this.onVectorHandleMouseUp.bind(this);
	}

	override onRemove() {
		if (this.el) {
			Services.Render.mount(this.el, null as any, this.name);
		}
		if (this.overlay) {
			this.overlay.setMap(null);
		}
	}

	@action.bound private updateState() {
		// getBounds() is null until the map's first idle, and setCapture() drives onDraw() directly
		// (outside Google's draw cycle) — skip the sync until real bounds exist rather than deref null.
		const bounds = this.options.map.getBounds();
		if (bounds == null) {
			return;
		}
		this.setZoom(this.options.map.getZoom());
		this.setBounds(bounds);
	}

	@action setZoom(zoom: number | undefined) {
		this.zoom = zoom;
	}

	@action setBounds(bounds: google.maps.LatLngBounds) {
		const newBounds = bounds.toUrlValue();
		if (newBounds === this.boundString.get()) {
			return;
		}
		this.bounds = bounds;
		this.boundString.set(newBounds);
	}

	onDraw() {
		const reset = this.verifyProjection();

		this.updateState();

		if (reset && this.el != null) {
			Services.Render.render(this.el, this.view());
		}
	}

	@action public setCollections(newCollections: CollectionModel[]) {
		this.collections = newCollections.filter(Boolean);
	}

	@action public setSelectedCollectionRevision(revision: string | null) {
		this.selectedCollectionRevision = revision;
	}

	@action public setSelectedVectorId(id: string | null) {
		this.selectedVectorId = id;
	}

	@action public hide() {
		this.visible = false;
		this.overlay.setMap(null);
	}

	@action public show() {
		this.visible = true;
		this.overlay.setMap(this.options.map);
	}

	@action public setInitialMove(coords: Point) {
		this.selectedVectorInitialMove = coords;
	}

	@action public setInferredMoveDelta(coords: Point) {
		const initial = this.selectedVectorInitialMove;
		if (initial == null) {
			return;
		}
		this.selectedVectorMoveDelta = {
			x: coords.x - initial.x,
			y: coords.y - initial.y,
		};
	}

	@action public setCapture(capture: CaptureFileJSONObject | null) {
		this.selectedCapture = capture;

		this.onDraw();
	}

	@action public resetMoveDelta() {
		this.selectedVectorMoveDelta = {
			x: 0,
			y: 0,
		};
	}

	@action private onVectorHandleMouseDown(event: MouseEvent) {
		if (this.measurementState && this.measurementState.isActive && !this.measurementState.isPaused) {
			return;
		}
		if (this.distanceMeasureState && this.distanceMeasureState.isActive) {
			return;
		}

		if (this.options.onVectorDragStart) {
			this.options.onVectorDragStart();
		}

		this.setInitialMove(pick(event, ['x', 'y']));

		window.addEventListener('mouseup', this.boundVectorHandleMouseUp);
		window.addEventListener('mousemove', this.boundVectorHandleMouseMove);
	}

	@action private onVectorHandleDragMove(event: MouseEvent) {
		this.setInferredMoveDelta(pick(event, ['x', 'y']));
	}

	@action private onVectorHandleMouseUp() {
		window.removeEventListener('mouseup', this.boundVectorHandleMouseUp);
		window.removeEventListener('mousemove', this.boundVectorHandleMouseMove);

		// See if any of the deltas have changed (ie not 0);
		if (Object.values(this.selectedVectorMoveDelta).some(Boolean)) {
			const revision = this.selectedCollectionRevision;
			if (revision == null) {
				return;
			}

			const vector = TopologicalPointCollectionCache.getCollectionVectors(revision).find(
				(k) => revision + k.field.id === this.selectedVectorId
			);
			if (vector == null) {
				return;
			}

			const { x, y } = PointCache.getPoint(vector.target.latitude, vector.target.longitude);

			const terminal = new google.maps.Point(x + this.selectedVectorMoveDelta.x, y + this.selectedVectorMoveDelta.y);

			const latLng = this.overlay.getProjection().fromDivPixelToLatLng(terminal);
			if (latLng == null) {
				return;
			}

			const nextVector = IkeGeodesic.getVector(vector.field.collection.location, {
				latitude: latLng.lat(),
				longitude: latLng.lng(),
				altitude: 0,
			});

			// The cache stores anchor fields under the vector type — widen here so
			// the caller's type guard can narrow correctly.
			const field = vector.field;
			if (this.options.onVectorDragEnd) {
				this.options.onVectorDragEnd(field, nextVector);
			} else if (CollectionModelFieldAnchor.isAnchorField(field)) {
				// Defensive fallback (no callback wired) — treat the drag as a manual
				// bearing edit on the anchor: force 'Other', clear span links.
				field.Mutator.manual({
					...nextVector,
					collectionTool: 'VECTOR',
					placement: 'Other',
					relatedSpanIds: [],
				});
			} else {
				field.Mutator.manual(nextVector);
			}

			TopologicalPointCollectionCache.dropCollectionVectors(revision);

			this.resetMoveDelta();
		}
	}

	@action public setCollectionHoveredRevision(revision: string | null) {
		this.collectionHoverRevision = revision;
	}

	@action public setVectorHoverId(id: string | null) {
		this.collectionAndVectorHoverId = id;
	}

	@action public setHideTitles(hide: boolean) {
		this.hideTitles = hide;
	}

	@action public setMeasurementState(state: MeasurementState) {
		this.measurementState = state;
	}

	private verifyProjection(): boolean {
		const projection = this.overlay.getProjection();
		if (projection == null) {
			PointCache.reset();
			return true;
		}

		PointCache.setProjection(projection);

		const point = projection.fromLatLngToDivPixel(this.origin);
		if (point == null) {
			PointCache.reset();
			return true;
		}

		const oldPoint = PointCache.getPoint(this.origin.lat(), this.origin.lng());

		if (point.x !== oldPoint.x || point.y !== oldPoint.y) {
			PointCache.reset();

			return true;
		}

		return false;
	}

	canSeeFieldOfView(): boolean {
		return Services.Token.has(IkeToken.FEATURE_MAP_FIELD_OF_VIEW);
	}

	get nodes() {
		this.verifyProjection();

		// Read once per render: constant for the whole pass, and getZoom crosses the Maps API boundary
		const zoom = this.currentMapZoom();
		const showTitlesOnMap = this.showCollectionTitlesOnMap(zoom);

		// Resolve each collection and its cached vectors once; the fore-span key pass and the vnode
		// build below share this
		const entries = this.collections.map((col) => {
			const collection = (AppStore.Collection.has(col.revision) ? AppStore.Collection.get(col.revision) : null) ?? col;
			return { collection, vectors: TopologicalPointCollectionCache.getCollectionVectors(collection) };
		});

		// Endpoint keys of every forespan — a backspan matching one in reverse hides its label
		const foreSpanKeys = new Set<string>();
		for (const { vectors } of entries) {
			for (const vector of vectors) {
				if (vector.spanRole === SpanType.NextPole && vector.field.value) {
					foreSpanKeys.add(TopologicalEditorMapOverlay.lineEndpointKey(vector.source, vector.target));
				}
			}
		}

		const lineLayers: MithrilVNode[] = [];
		const labelNodes: MithrilVNode[] = [];
		const pointLayers: MithrilVNode[] = [];

		for (const { collection, vectors } of entries) {
			const location = collection.location;

			const point = PointCache.getGeoPoint({
				type: 'point',
				coordinates: [get(location, ['longitude'], 0), get(location, ['latitude'], 0)],
			});

			const lines = vectors
				// Check to make sure that the vectors that are there actually exist before drawing them
				.filter((vector) => vector.field.value)
				.map((vector) => {
					if (!this.isLegendItemVisible(legendItemIdForSpanKind(vector.kind))) {
						return null;
					}

					const vectorId = collection.revision + vector.field.id;
					const isSelected =
						this.selectedVectorId === vectorId && vector.field.collection.revision === this.selectedCollectionRevision;
					const isHovered = this.collectionAndVectorHoverId === vectorId;

					// A backspan coincident with a rendered forespan defers to the forespan's label
					const suppressLabel =
						vector.spanRole === SpanType.PreviousPole &&
						foreSpanKeys.has(TopologicalEditorMapOverlay.lineEndpointKey(vector.target, vector.source));

					return new MapLine({
						vector,
						kind: vector.kind,
						initialPoint: point,
						isSelected,
						drawLabels: this.showVectorLabelsOnMap(vector.kind, zoom),
						showVectorLabel: isHovered || this.selectedVectorId === vectorId,
						suppressLabel,
						onClick: () => {
							this.options.onVectorClick(collection.revision, vector.field.id);

							this.setSelectedVectorId(vectorId);
						},
						onVectorMouseOver: () => {
							this.setVectorHoverId(vectorId);
						},
						onVectorMouseOut: () => {
							this.setVectorHoverId(null);
						},
						onVectorHandleMouseDown: vector.field.isCollectionLink()
							? noop
							: (event) => {
									event.preventDefault();
									event.stopPropagation();

									this.onVectorHandleMouseDown(event);
								},
						vectorHandleMoveDelta: this.selectedVectorMoveDelta,
						// A variant is read-only: keep its vectors selectable for inspection, but never render the
						// drag handle, so its unlinked spans and anchors can't be edited or snapped (UFO-1256).
						readOnly: isCollectionVariant(collection),
					});
				})
				.filter((line): line is MapLine => line != null);

			const poleAccuracy = TopologicalPointCollectionCache.getCollectionPoleAccuracy(collection);
			const isCollectionSelected = collection.revision === this.selectedCollectionRevision;
			const isCollectionHovered = this.collectionHoverRevision === collection.revision;
			// A pole is exempt from the focus dim while it's selected, hovered, or owns the focused vector
			const focusedVectorIds = [this.activeSelectedVectorId, this.collectionAndVectorHoverId];
			const ownsFocusedVector = vectors.some((vector) =>
				focusedVectorIds.includes(collection.revision + vector.field.id)
			);

			// The selected pole always renders, even when its accuracy layer is hidden in the legend
			const pointView =
				isCollectionSelected || this.isLegendItemVisible(legendItemIdForPoleAccuracy(poleAccuracy))
					? MapPoint.view({
							collection,
							point,
							isCollectionSelected,
							poleAccuracy,
							isFocusExempt: isCollectionSelected || isCollectionHovered || ownsFocusedVector,
							onClick: () => {
								if (CurrentState.CollectionLink.isLinking) {
									// A variant is a read-only link target and is refused (warn, stay in linking mode).
									if (!CurrentState.CollectionLink.completeLinking(collection)) {
										NotificationService.warn({ key: T.SPAN_LINK_VARIANT_TARGET });
									}
									return;
								}
								this.options.onCollectionClick(collection.revision);

								this.setSelectedVectorId(null);
							},
							onCollectionMouseOver: () => {
								this.setCollectionHoveredRevision(collection.revision);
							},
							onCollectionMouseOut: () => {
								this.setCollectionHoveredRevision(null);
							},
							showCollectionTitle: isCollectionHovered || isCollectionSelected,
							showTitlesOnMap,
						})
					: null;

			if (pointView != null) {
				pointLayers.push(
					m(
						'div',
						{
							key: `layer-point-${collection.revision}`,
							className: classNames(CSS.point, {
								[CSS.hidden]: !this.visible,
							}),
						},
						pointView
					)
				);
			}

			const polylines: MapPolyline[] = [];
			collection.savedLineFields().forEach((field) => {
				// Skip line features whose type is hidden via the legend eye (visibility is a display filter).
				const lineTypeOption = field.schema.findLineType(field.lineType);
				if (lineTypeOption != null && !this.isLineTypeVisible(lineTypeVisibilityKey(lineTypeOption))) {
					return;
				}
				polylines.push(
					new MapPolyline({
						field,
						onClick: () => {
							this.options.onCollectionClick(collection.revision);
							this.setSelectedVectorId(null);
						},
					})
				);
			});

			// Line-draw polylines render among the line layers (below points). Pushed before the span
			// line so the final lineLayers.reverse() keeps the span line above its polyline per collection.
			if (polylines.length) {
				lineLayers.push(
					m(
						'div',
						{
							key: `layer-polyline-${collection.revision}`,
							className: classNames(CSS.line, {
								[CSS.hidden]: !this.visible || !this.viewLines,
							}),
						},
						polylines.map((x) => x.nodes).filter(Boolean)
					)
				);
			}

			if (lines.length) {
				lineLayers.push(
					m(
						'div',
						{
							key: `layer-line-${collection.revision}`,
							className: classNames(CSS.line, {
								[CSS.hidden]: !this.visible || !this.viewVectors,
							}),
						},
						lines.map((x) => x.lineNodes)
					)
				);
				for (const line of lines) {
					const label = line.label;
					if (label != null) {
						labelNodes.push(label);
					}
				}
			}
		}

		// All line layers render below all point layers; line layers keep the last-collection-first
		// order the previous prepend-accumulator produced
		lineLayers.reverse();
		labelNodes.reverse();
		// Labels render in their own layer ABOVE all line layers (so no line covers a label) and below
		// the point layers (so pole markers/titles keep their current stacking).
		const output: MithrilVNode[] = [...lineLayers];
		if (labelNodes.length) {
			output.push(
				m(
					'div',
					{
						key: 'layer-labels',
						className: classNames(CSS.labelLayer, {
							[CSS.hidden]: !this.visible,
						}),
					},
					labelNodes
				)
			);
		}
		output.push(...pointLayers);

		// Add measurement nodes
		output.push(...this.measurementNodes);

		// Add distance measure nodes
		output.push(...this.distanceMeasureNodes);

		output.push(...this.lineDrawNodes);

		const projection = this.overlay.getProjection();
		if (
			this.selectedCapture &&
			this.selectedCapture.device &&
			this.canSeeFieldOfView() &&
			projection &&
			this.isLegendItemVisible('photoLocation') &&
			(this.bypassLabelZoomGating || isLabelTypeVisibleAtZoom('photoLocation', zoom))
		) {
			const { latitude, longitude } = this.selectedCapture.device;

			const point = projection.fromLatLngToDivPixel(
				new google.maps.LatLng({
					lat: latitude,
					lng: longitude,
				})
			);

			if (point != null) {
				const photomeasureNodes = m(
					'div',
					{
						className: CSS.container,
						style: {
							left: `${point.x}px`,
							top: `${point.y}px`,
							zIndex: -1,
						},
					},
					[
						m(
							'div',
							{
								className: CSS.fov,
							},
							m('img', {
								src: assetPath('/images/fov-cone.svg'),
								className: CSS.fovSprite,
								style: {
									transform: `rotate(${this.selectedCapture.orientation.bearing}rad)`,
								},
							})
						),
						m(
							'div',
							{
								className: CSS.orca,
							},
							m('img', {
								src: assetPath('/images/ike-device.svg'),
								className: CSS.orcaIcon,
								style: {
									transform: `rotate(${this.selectedCapture.orientation.bearing}rad)`,
								},
							})
						),
						m(
							'div',
							{
								className: classNames(CSS.labelPill, CSS.label),
							},
							// display(null) and display(NaN) both return '' — NaN keeps today's output
							IkeUnit.display(this.selectedCapture.distance ?? Number.NaN, Services.UserSettings.units)
						),
					]
				);

				output.push(photomeasureNodes);
			}
		}

		// Single wrapper so the focus dim is one class write; the scoped rules in Style do the rest
		return m(
			'div',
			{
				className: classNames(CSS.layers, {
					[CSS.layersFocusActive]: this.isFocusActive && !this.suppressFocusDim,
				}),
			},
			output.filter(Boolean)
		);
	}

	override view() {
		return m.fragment({}, [this.nodes]);
	}

	toggleViewTitles(): void {
		const next = !this.viewCollectionLabels;
		this.viewCollectionLabels = next;
		this.viewVectorLabels = next;
		this.persistDisplaySettings();
	}

	@action public toggleLegendItem(id: LegendItemId): void {
		this.hiddenLegendItems = this.hiddenLegendItems.includes(id)
			? this.hiddenLegendItems.filter((item) => item !== id)
			: [...this.hiddenLegendItems, id];
		this.persistDisplaySettings();
	}

	public isLegendItemVisible(id: LegendItemId | null): boolean {
		return id == null || !this.hiddenLegendItems.includes(id);
	}

	@action public toggleLineType(key: string): void {
		this.hiddenLineTypes = this.hiddenLineTypes.includes(key)
			? this.hiddenLineTypes.filter((item) => item !== key)
			: [...this.hiddenLineTypes, key];
		this.persistDisplaySettings();
	}

	public isLineTypeVisible(key: string): boolean {
		return !this.hiddenLineTypes.includes(key);
	}

	private persistDisplaySettings(): void {
		writeDisplaySettings({
			hiddenLegendItems: this.hiddenLegendItems,
			hiddenLineTypes: this.hiddenLineTypes,
			viewLabels: this.viewCollectionLabels,
		});
	}

	/** The selected vector id, only while its collection is still the selected one (matches line isSelected). */
	private get activeSelectedVectorId(): string | null {
		const revision = this.selectedCollectionRevision;
		if (this.selectedVectorId == null || revision == null) {
			return null;
		}
		const isSelectedCollectionVector = TopologicalPointCollectionCache.getCollectionVectors(revision).some(
			(vector) => revision + vector.field.id === this.selectedVectorId
		);
		return isSelectedCollectionVector ? this.selectedVectorId : null;
	}

	/** True while something is hovered or selected — everything else on the map dims. */
	@computed get isFocusActive(): boolean {
		return (
			this.activeSelectedVectorId != null ||
			this.collectionAndVectorHoverId != null ||
			this.collectionHoverRevision != null
		);
	}

	/**
	 * Zoom read straight off the map, NOT the `zoom` observable: the overlay's view renders inside
	 * a MobX autorun, and observing zoom would re-render mid-zoom-animation with pixel positions
	 * that disagree with the pane's interim transform (icons drift, then snap back). Every zoom
	 * change fires a `draw` whose synchronous render picks the new value up.
	 */
	private currentMapZoom(): number | undefined {
		return this.options.map.getZoom();
	}

	showCollectionTitlesOnMap(zoom: number | undefined): boolean {
		return (
			!this.hideTitles &&
			this.viewCollectionLabels &&
			this.isLegendItemVisible('collectionLabel') &&
			(this.bypassLabelZoomGating || isLabelTypeVisibleAtZoom('pole', zoom))
		);
	}

	showVectorLabelsOnMap(kind: MaybeSpanType, zoom: number | undefined): boolean {
		return (
			!this.hideTitles && this.viewVectorLabels && (this.bypassLabelZoomGating || isLabelTypeVisibleAtZoom(kind, zoom))
		);
	}

	/** Endpoint key for a span line; linked reciprocal spans produce exactly mirrored keys. */
	private static lineEndpointKey(a: LocationData, b: LocationData): string {
		return `${a.latitude.toFixed(6)},${a.longitude.toFixed(6)}|${b.latitude.toFixed(6)},${b.longitude.toFixed(6)}`;
	}

	private worldPointToPixel(latLng: google.maps.LatLng): google.maps.Point | null {
		const projection = this.overlay.getProjection();
		if (!projection) {
			return null;
		}

		return projection.fromLatLngToDivPixel(latLng);
	}

	private renderDraggablePoint(
		state: MeasurementState | DistanceMeasureState | LineDrawState,
		point: MeasurementPoint,
		keyPrefix: string,
		onClick?: (e: Event) => void,
		title?: string
	): MithrilVNode | null {
		const pixel = this.worldPointToPixel(point.location);
		if (!pixel) {
			return null;
		}
		return this.pointDragger.pointNode({ state, point, pixel, keyPrefix, onClick, title });
	}

	private renderMeasurementLine(
		start: MeasurementPoint,
		end: MeasurementPoint,
		keyPrefix = 'measurement',
		style?: { color?: string; strokeWidth?: string; dashArray?: string; lineCap?: string; showLabel?: boolean }
	): MithrilVNode[] | null {
		const startPixel = this.worldPointToPixel(start.location);
		const endPixel = this.worldPointToPixel(end.location);

		if (!startPixel || !endPixel) {
			return null;
		}

		const line = GenericMapLine.view({
			key: `${keyPrefix}-line-${start.id}-${end.id}`,
			start: startPixel,
			end: endPixel,
			color: style?.color ?? '#FFFFFF',
			strokeWidth: style?.strokeWidth ?? '1px',
			dashArray: style?.dashArray,
			lineCap: style?.lineCap,
		});

		if (style?.showLabel === false) {
			return [line];
		}

		const startLoc = { latitude: start.location.lat(), longitude: start.location.lng(), altitude: 0 };
		const endLoc = { latitude: end.location.lat(), longitude: end.location.lng(), altitude: 0 };
		const vector = IkeGeodesic.getVector(startLoc, endLoc);
		const distance = IkeUnit.display(vector.distance, Services.UserSettings.units);

		const midX = (startPixel.x + endPixel.x) / 2;
		const midY = (startPixel.y + endPixel.y) / 2;

		const label = m(
			'div',
			{
				key: `${keyPrefix}-label-${start.id}-${end.id}`,
				className: CSS.labelPill,
				style: {
					left: `${midX}px`,
					top: `${midY}px`,
					transform: 'translate(-50%, -50%)',
					zIndex: 1,
				},
			},
			distance
		);

		return [line, label];
	}

	// ── Tool-specific node getters ──

	private get measurementNodes(): MithrilVNode[] {
		const measurementState = this.measurementState;
		if (!measurementState || measurementState.points.length === 0) {
			return [];
		}

		const points = measurementState.points;
		const lineNodes = points.slice(0, -1).flatMap((point, index) => {
			const nextPoint = points[index + 1];
			if (!nextPoint) return [];
			return this.renderMeasurementLine(point, nextPoint) || [];
		});

		const pointNodes = points
			.map((point) =>
				this.renderDraggablePoint(
					measurementState,
					point,
					'measurement',
					(e: Event) => {
						e.stopPropagation();
						measurementState.removePoint(point.id);
					},
					Translate(T.MEASUREMENT_CLICK_TO_REMOVE)
				)
			)
			.filter(Boolean);

		return [...lineNodes, ...pointNodes];
	}

	private get distanceMeasureNodes(): MithrilVNode[] {
		const distanceMeasureState = this.distanceMeasureState;
		if (!distanceMeasureState || distanceMeasureState.points.length === 0) {
			return [];
		}

		const points = distanceMeasureState.points;
		const pointNodes = points
			.map((point) => this.renderDraggablePoint(distanceMeasureState, point, 'distance-measure'))
			.filter(Boolean);

		if (points.length >= 2) {
			const lineNodes = this.renderMeasurementLine(points[0], points[1], 'distance-measure') || [];
			return [...lineNodes, ...pointNodes];
		}

		return pointNodes;
	}

	private get lineDrawNodes(): MithrilVNode[] {
		if (!this.lineDrawState) {
			return [];
		}
		return buildLineDrawNodes(this.lineDrawState, this.pointDragger, (loc) => this.worldPointToPixel(loc), 'line-draw');
	}
}
